import useCharityMeals from '@/helpers/features/charityMeals'
import InfoIcon from '@mui/icons-material/Info'
import { Box, Skeleton, Stack, Tooltip, Typography } from '@mui/material'

import { debounce } from 'lodash'
import type React from 'react'
import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { useCartReorder } from '../../store/cartStore'
import type { ConsolidatedBasketProps, Item } from '../consolidated-basket'
import ReorderBasketItem from './basket-item'
import BasketLoadingOverlay from './basket-loading-overlay'

type ReorderBasketProps = ConsolidatedBasketProps & {
  reorderSection?: React.ReactNode
  hasActiveBasket?: boolean
  /** Team-order baskets render the panel in the tenant's secondary palette. */
  colorVariant?: 'primary' | 'secondary'
  /** Team-order participant strip, rendered directly above the input bar. */
  participantSection?: React.ReactNode
}

const ReorderBasket: React.FC<ReorderBasketProps> = ({
  fees,
  taxRates,
  headerSection,
  footerSection,
  errorSection,
  items,
  summary,
  taxAcronym,
  dataIsUpdating,
  loadingNewData = false,
  maxHeight,
  onDeleteClick,
  onQuantityChange,
  onNumberedOptionQuantityChange,
  onNoteChange,
  showItemNotes,
  inputBarSection,
  reorderSection,
  unavailableItemIds = [],
  hasActiveBasket = true,
  colorVariant = 'primary',
  participantSection,
}) => {
  const isEmptyBasket = items.length < 1
  const { t } = useTranslation()
  const { loadState } = useCartReorder()
  const isLoadingReorder = loadState === 'loading'
  const charityMealsEnabled = useCharityMeals()

  // Mirror V1: debounce direct quantity writes so rapid +/- clicks coalesce.
  // The handler is created once and reads the latest onQuantityChange via a
  // ref — useCallback(debounce(...), [onQuantityChange]) would recreate the
  // debounced fn (and cancel pending calls) on every basket update, since
  // the parent's handleQuantityChange is rebuilt on each basketData change.
  const onQuantityChangeRef = useRef(onQuantityChange)
  useEffect(() => {
    onQuantityChangeRef.current = onQuantityChange
  }, [onQuantityChange])

  const debouncedOnQuantityChange = useMemo(
    () =>
      debounce((itemId: string, qty: number) => {
        onQuantityChangeRef.current(itemId, qty)
      }, 100),
    [],
  )

  useEffect(() => {
    return () => {
      debouncedOnQuantityChange.cancel()
    }
  }, [debouncedOnQuantityChange])

  return (
    <Stack
      sx={{
        maxHeight,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: `${colorVariant}.main`,
        borderRadius: '20px',
        padding: '16px 14px 14px',
        position: 'relative',
      }}>
      {headerSection}

      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: '12px',
          // Allow shrink (minHeight:0) so this body can
          // actually scroll within maxHeight instead of the whole panel
          // growing past the card. Default flex min-height:auto would let
          // content force the panel taller than its container.
          overflowY: isLoadingReorder ? 'hidden' : 'auto',
          overflowX: 'hidden',
          scrollbarWidth: 'thin',
          // While the reorder overlay is showing, the stale pre-load items are
          // hidden (see the !isLoadingReorder guard on the items block below),
          // so the body would otherwise collapse to the reorder/input sections.
          // Let it shrink-wrap the overlay's own content (`auto`) rather than
          // pinning a fixed floor — a fixed height looked over-padded when the
          // prior order was large. The overlay no longer stretches to fill (it
          // sizes to its content), so the body height follows the animation.
          minHeight: isLoadingReorder ? 'auto' : 0,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}>
        {reorderSection}
        {participantSection}
        {inputBarSection}

        {/* Loading skeleton while the basket body fetches. The empty-state
            message is rendered as its own card below this box (see sibling
            after the inner Box) so it reads as a separate panel, per design. */}
        {loadingNewData ? (
          <Box sx={{ paddingX: 1.5 }}>
            <Stack spacing={1} sx={{ paddingY: 1.5 }}>
              <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
            </Stack>
          </Box>
        ) : null}

        {hasActiveBasket && !isEmptyBasket && !loadingNewData && !isLoadingReorder ? (
          <>
            <Box
              sx={{
                paddingX: 1.5,
                flex: 1,
              }}>
              {items.map((item: Item) => {
                const isUnavailable = unavailableItemIds.includes(item.id)
                return (
                  <ReorderBasketItem
                    key={item.id}
                    {...item}
                    priceUpdating={dataIsUpdating}
                    onDeleteClick={onDeleteClick}
                    onNoteChange={onNoteChange}
                    showItemNotes={showItemNotes}
                    onQuantityChange={(_itemReference, newQuantity) => {
                      // Items with a 'numbers'-type option must re-pick their
                      // numbered values when quantity increases — route through
                      // the menu-item modal rather than firing a bare qty update.
                      const hasNumberOption = item.itemOptions.some(
                        (option) => option.type === 'numbers',
                      )
                      if (hasNumberOption && item.quantity < newQuantity) {
                        if (!item.coreItemId) return
                        onNumberedOptionQuantityChange(item.coreItemId)
                      } else {
                        debouncedOnQuantityChange(item.id, newQuantity)
                      }
                    }}
                    isUnavailable={isUnavailable}
                  />
                )
              })}
            </Box>

            {/* Totals breakdown */}
            <Stack
              spacing={0.625}
              sx={{
                padding: '10px 12px',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}>
              {taxRates?.map((tax) => (
                <Stack direction="row" justifyContent="space-between" key={tax.rate}>
                  <Typography sx={{ fontSize: 12.5, color: 'text.disabled' }}>
                    {t('marketplace:itemSubtotal')} ({taxAcronym} @ {tax.rate}%)
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: 'text.disabled' }}>
                    {dataIsUpdating ? <Skeleton variant="text" width={60} /> : tax.totalExTax}
                  </Typography>
                </Stack>
              ))}
              {fees?.map((fee) => (
                <Stack direction="row" justifyContent="space-between" key={fee.code}>
                  <Typography sx={{ fontSize: 12.5, color: 'text.disabled' }}>
                    {fee.description}
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: 'text.disabled' }}>
                    {dataIsUpdating ? <Skeleton variant="text" width={60} /> : fee.feeTotalExTax}
                  </Typography>
                </Stack>
              ))}
              <Stack direction="row" justifyContent="space-between">
                <Typography sx={{ fontSize: 12.5, color: 'text.disabled' }}>
                  {t('marketplace:total')} {taxAcronym}
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: 'text.disabled' }}>
                  {dataIsUpdating ? <Skeleton variant="text" width={60} /> : summary?.totalTaxValue}
                </Typography>
              </Stack>

              {charityMealsEnabled.enabled && (
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography sx={{ fontSize: 12.5 }}>
                    {t('marketplace:sinceYoureFeedingWell', {
                      charityContributionHeadcount: summary?.charityContributionHeadcount,
                    })}
                  </Typography>
                  <Tooltip title={t('marketplace:forEveryMealYou')} placement="left">
                    <InfoIcon color="info" sx={{ transform: 'scale(0.75)' }} />
                  </Tooltip>
                </Stack>
              )}
            </Stack>

            <Box sx={{ paddingX: 1 }}>{errorSection}</Box>

            <Box sx={{ position: 'sticky', bottom: 0, zIndex: 1, bgcolor: 'background.paper' }}>
              {footerSection}
            </Box>
          </>
        ) : null}

        <BasketLoadingOverlay open={isLoadingReorder} />
      </Box>

      {/* Empty-state message in its own card, separated from the panel above.
          Shows whenever there are no items — including before a draft basket
          exists (hasActiveBasket === false). */}
      {!loadingNewData && isEmptyBasket ? (
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: '12px',
            marginTop: 1.25,
            paddingX: 1.5,
            paddingY: 4,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
          <Typography variant="body2" align="center">
            {t('marketplace:yourBasketIsEmpty')}
          </Typography>
        </Box>
      ) : null}
    </Stack>
  )
}

export default ReorderBasket
